import {convertDashesToSlashes} from './dateHelper';

// Helper data based on availability specification
// C - Can check in or out
// I - Can only check in
// O - Can only check out
const changeOverCodes = {
    CHECK_IN_OR_OUT: 'C',
    CHECK_IN_ONLY: 'I',
    CHECK_OUT_ONLY: 'O',
    NO_ACTION_POSSIBLE: 'X'
};

const checkInAllowedCodes = ['C', 'I'];
const checkOutAllowedCodes = ['C', 'O'];

const availabilityCodes = {
    AVAILABLE: 'Y',
    UNAVAILABLE: 'N'
};

const stayIncrementCodes = {
    DAY: 'D',
    WEEK: 'W',
    MONTH: 'M',
    YEAR: 'Y'
};

// Availabilty data gives time increment lengths
const stayIncrementLengths = {
    D: 1,
    W: 7,
    M: 30,
    Y: 365
};

const isAvailableCode = (code) => {
    return code === availabilityCodes.AVAILABLE;
};

class UnitAvailability {
    /* eslint-disable complexity */
    constructor(data = {}) {
        this.data = data;
        this.splitMinStayData = (
            (this.data.unitAvailabilityConfiguration || {}).minStay || ''
        ).split(',');
        this.splitMaxStayData = (
            (this.data.unitAvailabilityConfiguration || {}).maxStay || ''
        ).split(',');
        this.splitMinPriorNotifyData = (
            (this.data.unitAvailabilityConfiguration || {}).minPriorNotify || ''
        ).split(',');
        this.length = ((this.data.unitAvailabilityConfiguration || {}).availability || '').length; // this should suffice. date range and length of array should match.
    }
    /* eslint-enable complexity */

    getDefaults() {
        return {
            availability: this.data.availabilityDefault,
            changeOver: this.data.changeOverDefault,
            minStay: this.data.minStayDefault,
            maxStay: this.data.maxStayDefault,
            minPriorNotify: this.data.minPriorNotifyDefault,
            stayIncrement: this.data.stayIncrementDefault
        };
    }

    isWithinRange(index) {
        return index >= 0 && index < this.length;
    }

    getBeginDate() {
        const beginDateText = (this.data.dateRange || {}).beginDate;
        const beginDateUtc = new Date(beginDateText);
        const beginDateLocal = new Date(convertDashesToSlashes(beginDateText));

        return {
            text: beginDateText,
            utc: beginDateUtc,
            local: beginDateLocal
        };
    }

    getEndDate() {
        const endDateText = (this.data.dateRange || {}).endDate;
        const endDateUtc = new Date(endDateText);
        const endDateLocal = new Date(convertDashesToSlashes(endDateText));

        return {
            text: endDateText,
            utc: endDateUtc,
            local: endDateLocal
        };
    }

    getAvailabilityBlock(index, length) {
        let availability = this.data.unitAvailabilityConfiguration.availability;
        let defaultValue = this.data.availabilityDefault;

        if (index < 0) {
            return defaultValue;
        } else if (index + length > this.length) {
            // attach the default value to the end of the string if the dates go over the edge. one is enough.
            availability = `${availability}${defaultValue}`;
        }
        const substrLength = length
            ? length
            : this.data.unitAvailabilityConfiguration.availability.length - index;

        return availability.substr(index, substrLength);
    }

    getStayIncrementValue(index) {
        const isWithinRange = this.isWithinRange(index);
        const valueForIndex = this.data.unitAvailabilityConfiguration.stayIncrement[index];
        const defaultValue = this.data.stayIncrementDefault;
        return isWithinRange ? valueForIndex : defaultValue;
    }

    getMinStayValue(index) {
        const isWithinRange = this.isWithinRange(index);
        const valueForIndex = +this.splitMinStayData[index];
        const defaultValue = this.data.minStayDefault;
        return isWithinRange ? valueForIndex : defaultValue;
    }

    getMinStay({index, minValue = 0}) {
        let minStay = this.getMinStayValue(index);

        const length = stayIncrementLengths[this.getStayIncrementValue(index)];

        if (minStay < minValue) {
            minStay = minValue;
        }

        return length * minStay;
    }

    getMaxStayValue(index) {
        const isWithinRange = this.isWithinRange(index);
        const valueForIndex = +this.splitMaxStayData[index];
        const defaultValue = this.data.maxStayDefault;
        return isWithinRange ? valueForIndex : defaultValue;
    }

    getMaxStay(index) {
        let stayIncrement = this.getStayIncrementValue(index);
        let maxStayValue = this.getMaxStayValue(index);

        return stayIncrementLengths[stayIncrement] * +maxStayValue;
    }

    getMinPriorNotifyValue(index) {
        const isWithinRange = this.isWithinRange(index);
        const valueForIndex = +this.splitMinPriorNotifyData[index];
        const defaultValue = this.data.minPriorNotifyDefault;
        return isWithinRange ? valueForIndex : defaultValue;
    }

    getMinPriorNotify({index, minValue = 1}) {
        let minPriorNotify = this.getMinPriorNotifyValue(index);

        return Math.max(minValue, minPriorNotify);
    }

    getChangeOverValue(index) {
        const isWithinRange = this.isWithinRange(index);
        const valueForIndex = this.data.unitAvailabilityConfiguration.changeOver[index];
        const defaultValue = this.data.changeOverDefault;
        return isWithinRange ? valueForIndex : defaultValue;
    }

    getChangeoverBlock(index, length) {
        let changeOver = this.data.unitAvailabilityConfiguration.changeOver;
        let defaultValue = this.data.changeOverDefault;

        if (index < 0) {
            return defaultValue;
        } else if (index + length > this.length) {
            // attach the default value to the end of the string if the dates go over the edge. one is enough.
            changeOver = `${changeOver}${defaultValue}`;
        }

        return changeOver.substr(
            index,
            length ? length : this.data.unitAvailabilityConfiguration.changeOver.length - index
        );
    }

    isAvailable(index) {
        const isWithinRange = this.isWithinRange(index);
        const valueForIndex = this.data.unitAvailabilityConfiguration.availability[index];
        const defaultValue = this.data.availabilityDefault;
        const code = isWithinRange ? valueForIndex : defaultValue;
        const isAvailable = isAvailableCode(code);
        return isAvailable;
    }

    isCheckInDay(index) {
        const changeOverValue = this.getChangeOverValue(index);

        return checkInAllowedCodes.indexOf(changeOverValue) >= 0;
    }

    isCheckInAllowed(index) {
        let canCheckIn = this.isCheckInDay(index);

        // We don't want to call canMinStayBeMet for default values (if index -1) because we should use default values from UA
        if (canCheckIn && index >= 0) {
            canCheckIn = this.canMinStayBeMet({index, minValue: 1});
        }

        return canCheckIn;
    }

    isCheckOutDay(index) {
        const changeOverValue = this.getChangeOverValue(index);
        return checkOutAllowedCodes.indexOf(changeOverValue) >= 0;
    }

    minPriorNotifyMet(index, offset = 0) {
        // if index 0 is today, return true if index (number of days in the future) >= value (number of days notice required)
        // since index 0 might not be today, add an offset to account for that
        const minPriorNotify = this.getMinPriorNotify({index, minValue: 1});
        const minPriorNotifyMet = index - offset >= minPriorNotify;

        return minPriorNotifyMet;
    }

    canMinStayBeMet({index, minValue = 0}) {
        // If we can checkin, make sure it
        // 1) is available at least until min stay
        // 2) Has checkout date before it is no longer available.
        // For example, if min stay is 5 days, and it is booked 2 days later,
        // then we can't checkin.
        // Or, for a 5 day min stay, that we don't have only CheckIn days before it is booked.
        let minStay = this.getMinStay({index, minValue});
        let maxStay = this.getMaxStay(index);

        // Start by getting the array of data up until max stay.
        const allAvailability = this.getAvailabilityBlock(index, maxStay);

        // Start by seeing when the first unavailable date is...
        let firstUnavailable = allAvailability.indexOf(availabilityCodes.UNAVAILABLE);

        // It has to be AFTER the minStay, or not there at all.
        // firstUnavailable is 0'based, so it is >= to minStay.
        let canCheckIn = firstUnavailable < 0 || firstUnavailable >= minStay;

        if (canCheckIn) {
            // Now look to see that a valid checkout date is before the first unavailable one.
            if (firstUnavailable < 0) {
                firstUnavailable = allAvailability.length - 1;
            }

            const allChangeOver = this.getChangeoverBlock(index + minStay, maxStay);
            const firstC = allChangeOver.indexOf('C');
            const firstO = allChangeOver.indexOf('O');

            canCheckIn = firstC < firstUnavailable || firstO < firstUnavailable;
        }

        return canCheckIn;
    }

    isAvailableForRange(startIndex, endIndex) {
        let defaultValue = this.data.availabilityDefault;
        let isAvailable = true;

        if (!this.isWithinRange(startIndex)) {
            return isAvailableCode(defaultValue);
        }

        // Not evaluating endIndex availability value because endIndex
        // corresponds to the travelers checkout DAY, thus we don't care
        // whether the property is booked that NIGHT
        while (isAvailable && endIndex < this.length && startIndex < endIndex) {
            isAvailable = this.isAvailable(startIndex);
            startIndex++;
        }

        return isAvailable;
    }
}

export default UnitAvailability;
export {availabilityCodes, changeOverCodes, stayIncrementCodes, isAvailableCode, UnitAvailability};
